// External libraries
import React, { useEffect, useState } from 'react'
import classNames from 'classnames'

// UI Kit imports
import {
  BarLoader,
  BUTTON_THEME,
  Card,
  CARD_THEME,
  CheckBox,
  Input,
  INPUT_THEME,
} from '@st-operations/uikit/src/v2'
import pretty from '@st-operations/uikit/lib/pretty'

// Project components
import Header from '_components/header'
import Select from '_components/select'
import TemplateSelect from './TemplateSelect'
import PredictorsScore from '../../audience-creator/components/predictor-scores'

// Hooks, utilities, and constants
import { logErrorAsync } from '_lib/utility'
import { newBatchProcess } from '_services/batch-prebuillt-attributes'
import { usePrebuiltTopics } from '../../audience-creator/hooks/usePrebuiltTopics'
import { classifyPrebuiltAttributes } from '../../audience-creator/utils'
import { offlineAttributesModal } from '../../audience-creator/constants'
import { ENV_OPTIONS } from '../batch-prebuilt-attribute-processes'

// Styles
import './styles.scss'

const BatchPrebuiltAttributeSetup = (props) => {
  const [audienceTemplateId, setAudienceTemplateId] = useState(null)

  const [processName, setProcessName] = useState('')
  const [attributes, setAttributes] = useState(null)
  const [searchAttributes, setSearchAttributes] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('netwise')
  const [inRequest, setInRequest] = useState(false)
  const [predictorScoreHeader, setPredictorScoreHeader] = useState(null)
  const [env, setEnv] = useState(ENV_OPTIONS[0])

  const [templateOptions, setTemplateOptions] = useState([])

  const getProcessedAttributes = usePrebuiltTopics()

  useEffect(() => {
    setAttributes(getProcessedAttributes())
  }, [getProcessedAttributes])

  const classified = classifyPrebuiltAttributes(attributes ?? [])

  const handleTopicSearch = (val) => setSearchAttributes(val)

  const handleSelectCategory = (attrName) => () => {
    setSearchAttributes('')
    setSelectedCategory(attrName)
  }

  const handleCreate = () => {
    if (inRequest) return
    setInRequest(true)

    const selected = attributes
      .flatMap(({ topics }) => topics)
      .filter((topic) => topic.selected)
      .map((item) => ({
        type: item.type,
        atoms: item.atoms,
        name: item.topicName,
        ...(item.predictorScore ? { predictorScore: item.predictorScore } : {}),
      }))

    if (!selected.length) {
      toast.error('No attributes selected')
      setInRequest(false)
      return
    }
    if (!audienceTemplateId) {
      toast.error('Missing template audience')
      setInRequest(false)
      return
    }
    if (!processName) {
      toast.error('Missing name')
      setInRequest(false)
      return
    }

    const payload = {
      name: processName,
      audienceTemplateId,
      prebuiltAttributes: selected,
      env: env.value,
    }

    newBatchProcess(payload)
      .then((resp) => {
        toast.success('Audiences created')
        handleExit()
      })
      .catch((error) => {
        logErrorAsync(error)
        toast.error(error.message)
        setInRequest(false)
      })
  }

  const handleExit = () => {
    window._history.push(`/batch-prebuilt-attributes`)
  }

  const handleSelectTemplate = (audience) => {
    setProcessName(audience.name)
    setAudienceTemplateId(audience._id)
  }

  const handleName = (value) => setProcessName(value)

  const updateAttribute = (attr) =>
    setAttributes((prev) => {
      const newAttributes = [...prev]
      const category = newAttributes.find((cat) => cat.attrName === attr.attrName)
      const topic = category.topics.find((tpc) => tpc.topicName === attr.topicName)
      topic.selected = !topic.selected
      return newAttributes
    })

  const handleSelectTopic = (topic) => () => {
    if (Array.isArray(topic)) {
      if (currentTopics.every((t) => t.selected) || currentTopics.every((t) => !t.selected)) {
        topic.forEach((t) => updateAttribute(t))
      } else if (currentTopics.some((t) => t.selected)) {
        const unselected = topic.filter((t) => !t.selected)
        unselected.forEach((t) => updateAttribute(t))
      }
    } else if (topic) {
      updateAttribute(topic)
    }
  }

  const getCurrentTopics = () => {
    if (searchAttributes) {
      return attributes
        .flatMap(({ topics }) => topics)
        .filter((topic) => topic.topicName.includes(searchAttributes))
    }
    if (selectedCategory) {
      return attributes?.find(({ attrName }) => attrName === selectedCategory)?.topics ?? []
    }
    return []
  }

  const isOfflineAttribute = (selected) =>
    Object.values(offlineAttributesModal.attributeName).includes(selected)

  const handlePredictorScore = (selectedAttribute, selectedTopic = null) => ({
    target: { value },
  }) => {
    setAttributes((prevState) => {
      const attributesCopy = JSON.parse(JSON.stringify(prevState))
      const attribute = attributesCopy.find(({ attrName }) => attrName === selectedAttribute)

      if (selectedTopic) {
        attribute.topics.find(
          ({ topicName }) => topicName === selectedTopic.topicName
        ).predictorScore = value
      } else {
        setPredictorScoreHeader(value)
        attribute.topics.forEach((topic) => {
          if (topic.selected) topic.predictorScore = value
        })
      }

      return attributesCopy
    })
  }

  const currentTopics = getCurrentTopics()

  if (!attributes?.length) return <BarLoader />

  return (
    <div id="batch-attributes" className="batch-attributes">
      <Header
        title={
          <div className="batch-attributes__page-header">
            <span>Batch Prebuilt Attribute – Setup</span>
            <Select value={env} options={ENV_OPTIONS} onChange={(value) => setEnv(value)} />
          </div>
        }
        actions={[
          { children: 'Exit', onClick: handleExit, theme: BUTTON_THEME.GHOST },
          {
            children: inRequest ? 'Creating...' : 'Create Audiences',
            onClick: inRequest ? () => {} : handleCreate,
          },
        ]}
      />
      <div>
        <div className="batch-attributes__batch-details-block">
          <TemplateSelect handleSelectTemplate={handleSelectTemplate} />
          <Input
            label="Name"
            value={processName}
            onChange={handleName}
            description="Audiences will be created as '{name} > {attribute}'"
          />
        </div>
        <div className="batch-attributes__select-attributes-block">
          <div className="batch-attributes__header">
            <h3>Select Prebuilt Attributes</h3>
            <Input
              theme={INPUT_THEME.SEARCH}
              placeholder="Quick Search"
              className="modal-offline-attribute__header-search-input"
              classes={{ inputContainer: 'modal-offline-attribute__header-search' }}
              value={searchAttributes}
              onChange={handleTopicSearch}
            />
          </div>
          <div className="batch-attributes__nav">
            {Object.entries(classified).map(([key, attributes]) => (
              <div key={key} className="batch-attributes__group">
                <h4>{pretty(key)}</h4>
                {attributes.map(({ attrName }) => (
                  <button
                    key={attrName}
                    onClick={handleSelectCategory(attrName)}
                    className={classNames({
                      active: attrName === selectedCategory && !searchAttributes,
                    })}
                  >
                    {attrName}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div className="batch-attributes__main">
            <div className="batch-attributes__main-header">
              <CheckBox
                label={searchAttributes ? 'All Attributes' : selectedCategory}
                classes={{
                  label: 'batch-attributes__attribute-label',
                }}
                isIndeterminate={currentTopics.some((t) => t.selected)}
                checked={currentTopics.every((t) => t.selected)}
                onChange={handleSelectTopic(currentTopics)}
              />
              {/* <If condition={!isOfflineAttribute(selectedCategory) || searchAttributes}>
                <PredictorsScore
                  predictorScoreHeader={predictorScoreHeader}
                  handleChange={handlePredictorScore(selectedCategory)}
                />
              </If> */}
            </div>
            {currentTopics.map((topic) => (
              <Card
                key={topic.topicName}
                theme={CARD_THEME.NO_SHADOW}
                className="batch-attributes__attributes-card"
              >
                <CheckBox
                  data-topic={topic.topicName}
                  data-attribute={topic.attrName}
                  label={topic.topicName}
                  classes={{
                    label: topic.selected ? 'batch-attributes__selected-checkbox' : '',
                  }}
                  checked={topic.selected}
                  onChange={handleSelectTopic(topic)}
                />
                {/* <If condition={topic.selected && !isOfflineAttribute(topic.attrName)}>
                  <PredictorsScore
                    topic={topic}
                    handleChange={handlePredictorScore(topic.attrName, topic)}
                  />
                </If> */}
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BatchPrebuiltAttributeSetup
